import { useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useEventManagementService } from '../services/eventManagementService';
import type { EventTrace } from '../../../types/frontend';
import type { LiveEvent } from '../../../types/backend';

interface UseEventTraceResult {
  trace: EventTrace | null;
  loading: boolean;
  error: string | null;
  reload: () => Promise<void>;
}

// Query key for a single event's trace. Keyed by customer + event id + the
// event timestamp: the trace lookup is time-bounded (Logs Insights), so a given
// (id, timestamp) pair is a stable, cacheable entry.
export const eventTraceKeys = {
  trace: (customerId: string, eventId: string, timestamp: string) =>
    ['event-management', 'trace', customerId, eventId, timestamp] as const,
};

export const useEventTrace = (
  customerId: string,
  event: LiveEvent,
): UseEventTraceResult => {
  const eventService = useEventManagementService();

  const query = useQuery({
    queryKey: eventTraceKeys.trace(customerId, event.id, event.timestamp),
    queryFn: () => eventService.getEventTrace(customerId, event.id, event.timestamp),
  });

  const { refetch } = query;
  const reload = useCallback(async () => {
    await refetch();
  }, [refetch]);

  return {
    trace: query.data ?? null,
    loading: query.isFetching,
    error: query.error
      ? query.error instanceof Error
        ? query.error.message
        : 'Failed to load trace'
      : null,
    reload,
  };
};
